import React, { useEffect, useState, useRef } from "react";
import copy from "copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { useSelector } from "react-redux";
import { faGift, faXmark } from "@fortawesome/free-solid-svg-icons";
import Header from "../../../../components/Header";
import RefUsers from "./RefUsers";
import ReferralTermsAndFaq from "./referral-terms-and-faq";
import getReferralLink from "../../../../utils/getReferralLink";

function Ref() {
    const { userInfo } = useSelector((state) => state.auth);
    const [linkRef, setLinkRef] = useState(null);
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const timeoutRef = useRef(null);

    const onCopy = () => {
        if (!linkRef) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        copy(linkRef);
        setCopied(true);
        timeoutRef.current = setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    useEffect(() => {
        if (!userInfo) {
            setIsLoading(false);
            return;
        }

        try {
            const referralLink = getReferralLink(userInfo.userId);
            setLinkRef(referralLink);
        } catch (error) {
            console.error("Error generating referral link:", error);
            // Fallback to default link if error occurs
            setLinkRef(`https://longhousee.com/tai-app?ref=${userInfo.userId}`);
        } finally {
            setIsLoading(false);
        }

        // Cleanup timeout on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [userInfo]);

    return (
        <div className="h-dvh p-3 flex flex-col gap-4 md:w-[768px] lg:w-[1024px] md:mx-auto overflow-auto">
            <Header hasBackButton={true} />
            {copied && (
                <div className="fixed top-16 left-0 right-0 z-50 md:left-auto md:right-auto md:w-[768px] lg:w-[1024px] md:mx-auto animate-zoomIn">
                    <div className="mx-3">
                        <p className="text-center p-3 bg-primary-100 text-primary-700 rounded-lg">Sao chép thành công</p>
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-4 mt-4 pt-16">
                {/* Header Section */}
                <section className="bg-surface rounded-lg shadow p-2">
                    <div className="flex flex-col justify-center items-center gap-2 py-2">
                        <FontAwesomeIcon icon={faGift} className="text-3xl text-primary-500" />
                        <p className="font-bold text-lg">Giới thiệu LongHousee</p>
                        <span className="text-sm text-content-2">Nhận ngay hoa hồng khủng</span>
                    </div>
                </section>

                {/* Program Rules Section */}
                <section className="bg-surface rounded-lg shadow p-2 text-sm">
                    <p className="font-semibold mb-2">Thể lệ chương trình:</p>
                    <p className="text-content-2 mb-2">Chương trình chỉ áp dụng cho user có từ <span className="font-semibold">5 đơn hàng</span> được duyệt hoàn tiền thành công trên hệ thống Longhousee.</p>
                    <p className="text-content-2 mb-2">Mã giới thiệu chính là <span className="font-semibold">ID User</span> của bạn. Khi giới thiệu thành công, hệ thống sẽ lập tức tính ngay bạn mới cho bạn (xem tại danh sách đã giới thiệu phía bên dưới). Trường hợp chưa ghi nhận thành công, bạn cần hướng dẫn bạn mới mở app, sau đó vào Trang cá nhân sẽ có mục Nhập mã giới thiệu để bổ sung mã của bạn (khả dụng trong 7 ngày kể từ khi đăng ký tài khoản).</p>
                    <p className="text-content-2 mb-2">Khi giới thiệu người dùng thành công, bạn sẽ nhận được hoa hồng trên mỗi đơn hàng của người dùng mới. Hưởng quyền lợi tối đa 3 tháng kể từ khi giới thiệu user mới thành công.</p>
                    <p className="text-content-2">
                        Ví dụ: Bạn giới thiệu được A, và A phát sinh 1 đơn hàng và <span className="font-semibold">được duyệt hoàn tiền thành công</span> có mức hoàn tiền là 20.000đ từ Shopee, bạn sẽ nhận được một khoản thưởng là 20.000đ * 10% = 2.000đ.
                    </p>
                    <p className="italic text-content-3 text-xs">Chương trình áp dụng tới khi có thông báo mới.</p>
                </section>

                {/* Commission Rate Section */}
                    <section className="bg-surface rounded-lg shadow p-2 text-sm flex flex-col gap-3">
                    <p className="font-semibold">Mức hoa hồng thưởng:</p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2 px-2 font-semibold">Nền tảng</th>
                                    <th className="text-right py-2 px-2 font-semibold">Hoa hồng</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-2 px-2">Shopee</td>
                                    <td className="py-2 px-2 text-right font-semibold text-primary-600">10%</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-2">Lazada</td>
                                    <td className="py-2 px-2 text-right font-semibold text-primary-600">5%</td>
                                </tr>
                                <tr>
                                    <td className="py-2 px-2">TikTok Shop</td>
                                    <td className="py-2 px-2 text-right font-semibold text-primary-600">5%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Điều kiện & lưu ý + FAQ */}
                <ReferralTermsAndFaq />

                {/* Referral Link Section */}
                {userInfo && !isLoading && userInfo.refEligible && linkRef && (
                    <section className="bg-surface rounded-lg shadow p-2">
                        <p className="font-semibold mb-3 text-center">Link giới thiệu của bạn</p>
                        <div className="flex items-center bg-primary-50 border border-primary-200 rounded-lg overflow-hidden">
                            <p className="font-semibold p-3 truncate flex-1 text-sm text-primary-700">{linkRef}</p>
                            <button onClick={onCopy} className="p-3 text-content-2 hover:text-primary-600 hover:bg-primary-100 transition-colors" aria-label="Sao chép link">
                                <FontAwesomeIcon icon={faCopy} className="text-lg" />
                            </button>
                        </div>
                    </section>
                )}

                {userInfo && !isLoading && !userInfo.refEligible && (
                    <section className="bg-surface rounded-lg shadow p-2">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                            <p className="font-semibold text-yellow-800 mb-1">Chưa đủ điều kiện tham gia</p>
                            <p className="text-sm text-yellow-700">
                                Bạn cần có từ <span className="font-bold">5 đơn hàng</span> được duyệt hoàn tiền thành công để lấy link giới thiệu.
                                Hiện tại bạn có <span className="font-bold">{userInfo.approvedOrderCount || 0}/5</span> đơn.
                            </p>
                        </div>
                    </section>
                )}

                {userInfo && isLoading && (
                    <section className="bg-surface rounded-lg shadow p-2">
                        <p className="font-semibold mb-3 text-center">Link giới thiệu của bạn</p>
                        <div className="flex justify-center items-center bg-surface-muted text-content-3 rounded-lg p-3">
                            <p className="animate-pulse">Đang tải...</p>
                        </div>
                    </section>
                )}

                {/* Ref Users Section */}
                <RefUsers />
            </div>
        </div>
    );
}

export default Ref;
